import Analytics from '@opencare/analytics';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import { useAnalyticsService } from 'services/AnalyticsService';

import { useEffectOnce } from 'utils/customHooks/useEffectOnce';

import SingleSelect from '../../../../../common/components/SingleSelect/SingleSelect';
import {
  OptionBlock,
  Subtitle,
  Title,
} from '../../../../../common/styled-components';
import { useFunnelStorage } from '../../../../storage/Storage';
import { StepWrapper } from '../../../../styled-components';
import { generateStepAnalytics, STEP_KEY } from '../../../../utils';
import { SUBSTEPS } from '../../constants';

import { hasSocialAssistance, socialAssistanceExamples, sunlifeCdcpOptions } from './constants';

const analytics = new Analytics();

const SocialAssistance = ({ position, moveToNextStep, setSubStep }) => {
  const storage = useFunnelStorage();

  const analyticsService = useAnalyticsService();

  const location = storage.get(STEP_KEY.LOCATION);

  const country = useMemo(() => location?.address?.country, [location]);
  const state = useMemo(() => location?.address?.administrative_area_level_1, [location]);

  const isCanada = useMemo(() => country === 'Canada', [country]);

  const insurance = storage.get(STEP_KEY.INSURANCE);
  const isSunlifeSelected = useMemo(() => insurance?.provider === 'Sun Life Financial', [insurance]);

  const isCanadianSunlifeSelected = useMemo(() => isCanada && isSunlifeSelected, [isCanada, isSunlifeSelected]);

  const examplePrograms = useMemo(
    () => socialAssistanceExamples[state] || socialAssistanceExamples[country],
    [state, country],
  );

  useEffectOnce(() => {
    analytics.trackPageView({
      pageType: analytics.pageTypes.FUNNEL,
      pageName: 'funnel_social-assistance',
      ...generateStepAnalytics(position, storage),
      ...analyticsService.analyticsData,
    });
  });

  const handleClick = (option, index) => {
    analytics.trackClick({
      pageType: analytics.pageTypes.FUNNEL,
      pageName: 'funnel_social-assistance',
      name: 'wizard',
      section: 'social-assistance',
      position: [index + 1],
      ...generateStepAnalytics(position, storage, {
        response: option.id,
      }),
    });

    // Insurance is complete if they are not using social assistance
    const isComplete = option.data === false;

    const newInsuranceData = (() => {
      // If they have already selected an insurance provider, we just update the social assistance status
      if (insurance?.provider) {
        return {
          ...insurance,
          socialAssistance: option.data,
        };
      }

      return {
        hasInsurance: 'No',
        socialAssistance: option.data,
      };
    })();

    storage.save(
      STEP_KEY.INSURANCE,
      newInsuranceData,
      isComplete,
    );

    if (isComplete) {
      moveToNextStep();
    } else {
      setSubStep(SUBSTEPS.NOT_ELIGIBLE);
    }
  };

  const titleText = useMemo(
    () => isCanadianSunlifeSelected
      ? 'Do you have a CDCP (Canadian Dental Care Plan) through Sun Life?'
      : 'Will you use a social assistance program to pay for your visit?',
    [isCanadianSunlifeSelected],
  );

  const subtitleText = useMemo(() => {
    if (isCanadianSunlifeSelected) {
      return 'CDCP is a dental insurance plan that covers routine dental care for eligible individuals.';
    }

    if (examplePrograms) {
      return `e.g. ${examplePrograms.join(', ')}`;
    }

    return null;
  }, [isCanadianSunlifeSelected, examplePrograms]);

  const options = useMemo(
    () => isCanadianSunlifeSelected ? sunlifeCdcpOptions : hasSocialAssistance,
    [isCanadianSunlifeSelected],
  );

  return (
    <div data-testid="substep-social-assistance">
      <Title id="title">{titleText}</Title>

      <Subtitle data-testid="example-programs">
        {subtitleText}
      </Subtitle>

      <StepWrapper>
        <form>
          <OptionBlock row={2} className="sensitive-data">
            {options.map((option, i) => (
              <SingleSelect
                key={option.id}
                id={option.id}
                title={option.title}
                description={option.description}
                describedBy="title"
                onChange={() => handleClick(option, i)}
              />
            ))}
          </OptionBlock>
        </form>
      </StepWrapper>
    </div>
  );
};

SocialAssistance.defaultProps = {
  position: '',
};

SocialAssistance.propTypes = {
  position: PropTypes.string,
  moveToNextStep: PropTypes.func.isRequired,
  setSubStep: PropTypes.func.isRequired,
};

export default SocialAssistance;
